import {
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select as ChakraSelect,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { MdAddCircleOutline } from "react-icons/md";
import Select from "react-select";

import { Button, useToast } from "../../../components";
import useSelectTheme from "../../../hooks/useSelectTheme";
import { useSetMembershipsMutation } from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useUserSearchSelect } from "../../hooks/useUserSearchSelect";

interface AddUserToHiringTeamButtonProps {
  position: {
    id: string;
  };
  filterUserIds?: string[];
}

const AddUserToHiringTeamButton: React.FC<AddUserToHiringTeamButtonProps> = ({
  position,
  filterUserIds,
}) => {
  const currentUser = useCurrentUser();
  const toast = useToast();
  const [theme, styles] = useSelectTheme({
    container: (provided: Record<string, any>) => {
      return { ...provided, flex: 1 };
    },
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [roleId, setRoleId] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const {
    users,
    loading: usersLoading,
    onInputChange,
  } = useUserSearchSelect({
    excludeUserIds: filterUserIds,
    signUpCompleted: false,
  });

  const currentUserMembership = currentUser.memberships.find((m) => {
    return m.position.id === position.id;
  });
  const enabled =
    currentUser.userRole?.canManageHiringTeam ||
    currentUserMembership?.role?.canManageHiringTeam;
  const { positionRoles } = currentUser.organization;
  const [setMemberships, { loading: setMembershipsLoading }] =
    useSetMembershipsMutation({
      onCompleted: (data) => {
        if (data.setMemberships) {
          onClose();
          toast({
            title: "Success",
            description: "User added",
            status: "success",
          });
        }
      },
      onError: (err) => {
        toast({
          title: "Error",
          description: err.message,
          status: "error",
        });
      },
    });

  const onSubmit = (): void => {
    if (!roleId) return;
    if (!userId) return;
    setMemberships({
      variables: {
        users: [
          {
            userId,
            positionId: position.id,
            positionRoleId: roleId,
          },
        ],
      },
    });
  };

  useEffect(() => {
    if (positionRoles.length) {
      setRoleId(positionRoles[0].id);
    }
  }, [positionRoles]);

  return (
    <>
      <Button
        size="xs"
        variant="outline"
        leftIcon={<MdAddCircleOutline />}
        onClick={onOpen}
        disabled={!enabled}
      >
        Add user
      </Button>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>Add User</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing="8">
                <FormControl>
                  <FormLabel>User</FormLabel>
                  <Select
                    theme={theme}
                    styles={styles}
                    isClearable
                    placeholder="Select a User"
                    isLoading={usersLoading}
                    filterOption={() => true}
                    onInputChange={onInputChange}
                    options={users.map((u) => {
                      if (u.fullName === "") {
                        return {
                          label: `${u.email}`,
                          value: u.id,
                        };
                      }
                      return {
                        label: `${u.fullName} - ${u.email}`,
                        value: u.id,
                      };
                    })}
                    onChange={(option) =>
                      setUserId(
                        (option as { value: string } | undefined)?.value
                      )
                    }
                  />
                </FormControl>
                <FormControl mt="2">
                  <FormLabel>Role</FormLabel>
                  <ChakraSelect
                    value={roleId}
                    isDisabled={!userId}
                    onChange={(e) => setRoleId(e.target.value)}
                  >
                    {positionRoles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.formattedName}
                      </option>
                    ))}
                  </ChakraSelect>
                  <FormHelperText>
                    {
                      positionRoles.find((r) => r.id === roleId)
                        ?.formattedPermissions
                    }
                  </FormHelperText>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={onSubmit}
                isDisabled={!roleId || !userId}
                isLoading={setMembershipsLoading}
              >
                Submit
              </Button>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  );
};

export default AddUserToHiringTeamButton;
